import { motion } from "motion/react";
import { ArrowRight, Building2, ClipboardList, Search } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import { PageCard } from "@/components/AppFrame";

/**
 * Explainer page at `/welcome`. Surfaces from the IntakePage when no
 * tenant context could be resolved (bare apex / dev), and is the
 * destination of the "About EduViaOS" CTA on the empty-state cards.
 *
 * Per-tenant apply URLs (D2d) live at hosts like
 * `acme-school--apply.eduviaos.com` (production) or
 * `acme-school--apply-stage.eduviaos.com` (stage). This page exists
 * so a curious visitor who lands on the bare host gets a calm
 * explanation rather than a 404 / spinner.
 */
export function HomePage() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.32, 0.72, 0.32, 1] }}
    >
      <PageCard
        title="EduViaOS Admissions"
        subtitle="Submit and track your admission enquiry — no account, no password."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <FeatureRow
            icon={<Building2 className="h-4 w-4" />}
            title="Institution-specific intake"
            body="Each institution publishes a unique enquiry link from their own EduViaOS workspace."
          />
          <FeatureRow
            icon={<ClipboardList className="h-4 w-4" />}
            title="No account required"
            body="Fill in the form once. We'll give you a private tracking link to follow the progress."
          />
          <FeatureRow
            icon={<Search className="h-4 w-4" />}
            title="Always up-to-date"
            body="Status updates from the institution reach the tracker instantly — no email refresh needed."
          />
          <FeatureRow
            icon={<ArrowRight className="h-4 w-4" />}
            title="Have a link?"
            body="Open the URL the institution shared with you. Lost it? Ask them to resend the tracking link."
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" asChild>
            <a href="https://eduviaos.com" target="_blank" rel="noreferrer noopener">
              About EduViaOS
            </a>
          </Button>
        </div>
      </PageCard>
    </motion.div>
  );
}

function FeatureRow({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="border-border/60 bg-background/40 rounded-lg border p-3.5">
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className="bg-primary/10 text-primary inline-flex h-6 w-6 items-center justify-center rounded-md"
        >
          {icon}
        </span>
        <h2 className="text-foreground text-sm font-medium">{title}</h2>
      </div>
      <p className="text-muted-foreground mt-1.5 text-xs leading-relaxed">{body}</p>
    </div>
  );
}
